// Shared state, hooks and formatters for the /reports/* dashboards
// (screens/order-solicitudes and screens/order-purchases).

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as api from "../../utils/Api.ts";
import type { ReportesFiltros } from "../../utils/Api.ts";
import { InitDataContext } from "../../context/InitDataContext";
import { AlcanceReportes, alcanceReportes } from "./accesoReportes.ts";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

export interface CatalogoItem {
  id: number;
  codigo?: string;
  nombre: string;
}

export interface CentroCostoCatalogo extends CatalogoItem {
  idArea: number;
  area: string;
}

export interface Catalogos {
  estadosSolicitud: CatalogoItem[];
  estadosOrden: CatalogoItem[];
  centrosCosto: CentroCostoCatalogo[];
  areas: CatalogoItem[];
  tiposDocumento: CatalogoItem[];
}

// Lead-time stats (in days) returned by ciclo/tiempos and recepciones/resumen
export interface EstadisticaDias {
  cantidad: number;
  promedio: number | null;
  mediana: number | null;
  p90: number | null;
  minimo: number | null;
  maximo: number | null;
  descartados: number;
}

export interface TramoTiempo extends EstadisticaDias {
  codigo: string;
  nombre: string;
}

export interface TiemposCiclo {
  cantidadOrdenes: number;
  tramos: TramoTiempo[];
}

export interface FiltrosForm {
  fechaDesde: Date | null;
  fechaHasta: Date | null;
  centrosCosto: number[];
  idArea: number | null;
  idTipoDocumento: number | null;
  // Set by clicking a row / bar; the name is only for the chip label
  idUsuario: number | null;
  usuarioNombre: string;
  idProveedor: number | null;
  proveedorNombre: string;
  // Document numbers (the backend links solicitud <-> OC data both ways)
  idSolicitud: number | null;
  nroOrdenCompra: number | null;
}

/* ------------------------------------------------------------------ */
/* Catalogs                                                             */
/* ------------------------------------------------------------------ */

// Values must match the backend AgrupacionPeriodo enum
export const AGRUPACION_OPTIONS = [
  { label: "Semana", value: "semana" },
  { label: "Mes", value: "mes" },
  { label: "Trimestre", value: "trimestre" },
  { label: "Año", value: "anio" },
];

export const ORDENAR_POR_OPTIONS = [
  { label: "Monto", value: "monto" },
  { label: "Cantidad", value: "cantidad" },
];

const MESES_CORTOS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/* ------------------------------------------------------------------ */
/* Formatters                                                           */
/* ------------------------------------------------------------------ */

// Catalog names come padded from CHAR columns
export const clean = (text: string | null | undefined): string =>
  (text ?? "").trim();

// Local date -> YYYY-MM-DD (toISOString would shift the day by timezone)
export const toIsoDate = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// "2026-03-05" -> "05-03-2026" (date-only values, no timezone involved)
export const formatIsoDate = (value: string | undefined): string => {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

// Timestamps arrive as real UTC instants: show them in local time so a
// document created at 21:00 isn't displayed on the next day
export const formatFecha = (value: string | null | undefined): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// "2026-03" -> "Mar 2026"; other formats (week / quarter / year) as-is
export const formatPeriodo = (periodo: string): string => {
  const match = /^(\d{4})-(\d{1,2})$/.exec(String(periodo));
  if (match) {
    const mes = MESES_CORTOS[Number(match[2]) - 1];
    if (mes) return `${mes} ${match[1]}`;
  }
  return String(periodo);
};

export const formatDias = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "-"
    : `${Number(value).toLocaleString("es-CL", {
        maximumFractionDigits: 1,
      })} d`;

export const truncate = (text: string, max = 28) =>
  text && text.length > max ? `${text.substring(0, max - 1)}…` : text || "-";

// "ORDEN_EMITIDA" -> "Orden emitida"
export const formatEtapa = (etapa: string | null | undefined): string => {
  if (!etapa) return "-";
  const text = etapa.replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.substring(1);
};

// Approval state colors: the app-wide palette "COLORES DE ESTADISTICAS"
// in utils/main.css (same as the maintenance tables and legacy charts).
// Chart.js paints on a canvas and can't use var(--x), so the variables are
// resolved at runtime; the fallbacks mirror main.css.
const ESTADO_APROBACION_VARS: Record<string, [string, string]> = {
  PEND: ["--color-pendiente", "#4e4e4e"],
  ANUL: ["--color-anuladas", "#680902"],
  RECHP: ["--color-rechazadas-parcial", "#412b04"],
  RECH: ["--color-rechazadas", "#745419"],
  APROP: ["--color-aprobadas-parcial", "#7cb081"],
  APRO: ["--color-aprobadas", "#3f9648"],
  CONFP: ["--color-confirmadas-parcial", "#485d8b"],
  CONF: ["--color-confirmadas", "#223661"],
  PDGTE: ["--color-pendientes-gte", "#5f9ea0"],
  CFGTE: ["--color-confirmadas-gte", "#8a9796"],
  EMIT: ["--color-emitidas", "#5f9ea0"],
};

// State names as the API returns them (trazabilidad rows only carry names)
const ESTADO_APROBACION_POR_NOMBRE: Record<string, string> = {
  pendiente: "PEND",
  anulada: "ANUL",
  "rechazada parcialmente": "RECHP",
  rechazada: "RECH",
  "aprobada parcialmente": "APROP",
  aprobada: "APRO",
  "confirmada parcialmente": "CONFP",
  confirmada: "CONF",
  "pendiente gg": "PDGTE",
  "confirmada gg": "CFGTE",
  "confirmadas gg": "CFGTE",
  emitida: "EMIT",
};

const ESTADO_SIN_COLOR = "#94a3b8";

const readCssVar = (name: string, fallback: string): string => {
  try {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  } catch {
    return fallback;
  }
};

// Color for an approval state codigo (PEND, APRO, CONF, PDGTE, ...)
export const estadoAprobacionColor = (
  codigo: string | null | undefined,
): string => {
  const entry = ESTADO_APROBACION_VARS[clean(codigo).toUpperCase()];
  return entry ? readCssVar(entry[0], entry[1]) : ESTADO_SIN_COLOR;
};

// CSS variable of an approval state codigo, for DOM elements (var(--x))
export const estadoAprobacionCssVar = (
  codigo: string | null | undefined,
): string | undefined =>
  ESTADO_APROBACION_VARS[clean(codigo).toUpperCase()]?.[0];

// Same color, looked up by the state name ("Confirmada", "Pendiente GG", ...)
export const estadoAprobacionColorPorNombre = (
  nombre: string | null | undefined,
): string =>
  estadoAprobacionColor(
    ESTADO_APROBACION_POR_NOMBRE[clean(nombre).toLowerCase()],
  );

// Invoice payment states exactly as the finanzas maintenance table paints
// them (estadoConhisTemplate: PAGADA / PROCESADA "info", ANULADA "danger",
// PENDIENTE "secondary"); colors are the lara-light-cyan Tag backgrounds
// ("secondary" has no rule in that theme and falls back to .p-tag cyan)
export const ESTADO_PAGO_TAG: Record<
  string,
  { severity: "info" | "danger" | "secondary"; color: string }
> = {
  PAGADA: { severity: "info", color: "#0ea5e9" },
  PROCESADA: { severity: "info", color: "#0ea5e9" },
  ANULADA: { severity: "danger", color: "#ef4444" },
  PENDIENTE: { severity: "secondary", color: "#06b6d4" },
};

// INET states of ordenes_compra_consolidado_contable (same codes as the
// finanzas dashboard)
export type InetSeverity = "info" | "warning" | "success" | "secondary";

const ESTADO_INET: Record<
  number,
  { label: string; color: string; severity: InetSeverity }
> = {
  2: { label: "Emitida", color: "#2f7ed8", severity: "info" },
  3: { label: "Parcialmente cumplida", color: "#f0a202", severity: "warning" },
  4: { label: "Cumplida", color: "#1aa37a", severity: "success" },
  5: { label: "Anulada", color: "#94a3b8", severity: "secondary" },
};

export const inetInfo = (estado: number | null | undefined) =>
  estado !== null && estado !== undefined && ESTADO_INET[estado]
    ? ESTADO_INET[estado]
    : {
        label:
          estado === null || estado === undefined
            ? "Sin estado INET"
            : `Estado INET ${estado}`,
        color: "#94a3b8",
        severity: "secondary" as InetSeverity,
      };

export const errorMessage = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : "No fue posible cargar el reporte";

/* ------------------------------------------------------------------ */
/* Filters                                                              */
/* ------------------------------------------------------------------ */

export const quickRanges = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const quarterStart = Math.floor(m / 3) * 3;
  return [
    { label: "Mes actual", desde: new Date(y, m, 1), hasta: today },
    { label: "Trimestre", desde: new Date(y, quarterStart, 1), hasta: today },
    { label: "Año actual", desde: new Date(y, 0, 1), hasta: today },
    {
      label: "Últimos 12 meses",
      desde: new Date(y, m - 11, 1),
      hasta: today,
    },
    {
      label: "Año anterior",
      desde: new Date(y - 1, 0, 1),
      hasta: new Date(y - 1, 11, 31),
    },
  ];
};

export const defaultFiltros = (): FiltrosForm => {
  const today = new Date();
  return {
    fechaDesde: new Date(today.getFullYear(), 0, 1),
    fechaHasta: today,
    centrosCosto: [],
    idArea: null,
    idTipoDocumento: null,
    idUsuario: null,
    usuarioNombre: "",
    idProveedor: null,
    proveedorNombre: "",
    idSolicitud: null,
    nroOrdenCompra: null,
  };
};

/**
 * Common filters accepted by every /reports endpoint.
 *
 * Users with cost center 29 (alcance.total) have no scope: centrosCosto is
 * just their selection. Everyone else always sends their scope:
 *  - alcanceCentros: their centros_costo_vista
 *  - alcanceSolicitante (only when idSolicitante is given, i.e. on the
 *    solicitudes dashboard): also the solicitudes they created, in any cost
 *    center. The backend applies it to solicitud data only.
 * The backend ANDs centrosCosto (the user's picks) inside that scope.
 */
export const toQuery = (
  f: FiltrosForm,
  alcance?: AlcanceReportes,
  idSolicitante?: number,
): ReportesFiltros => {
  const restringido = !!alcance && !alcance.total;
  return {
    fechaDesde: toIsoDate(f.fechaDesde),
    fechaHasta: toIsoDate(f.fechaHasta),
    centrosCosto: f.centrosCosto.length ? f.centrosCosto.join(",") : undefined,
    alcanceCentros: restringido ? alcance.centros.join(",") : undefined,
    alcanceSolicitante:
      restringido && idSolicitante ? idSolicitante : undefined,
    idArea: f.idArea ?? undefined,
    idTipoDocumento: f.idTipoDocumento ?? undefined,
    idUsuario: f.idUsuario ?? undefined,
    idProveedor: f.idProveedor ?? undefined,
    idSolicitud: f.idSolicitud ?? undefined,
    nroOrdenCompra: f.nroOrdenCompra ?? undefined,
  };
};

interface CentroSolicitado {
  idCentroCosto: number;
}

interface OpcionesFiltros {
  // Users without cost center 29 also see what they requested in other cost
  // centers: their solicitudes (solicitudes dashboard) or the OC generated
  // from them (órdenes dashboard; needs the backend to widen order data)
  incluirSolicitudesPropias?: boolean;
}

/**
 * Editable filter form plus the applied copy that drives the queries.
 * Click-to-filter helpers (usuario / proveedor / centro de costo) apply
 * immediately, like the finanzas dashboard.
 */
export function useFiltrosReporte(opciones: OpcionesFiltros = {}) {
  // What the logged-in user may see (cost center 29 = everything)
  const { initData } = useContext(InitDataContext);
  const alcance = useMemo(() => alcanceReportes(initData), [initData]);
  const idUsuarioActual = Number(initData?.id) || undefined;
  const incluyePropias =
    !!opciones.incluirSolicitudesPropias && !alcance.total && !!idUsuarioActual;
  const idSolicitante = incluyePropias ? idUsuarioActual : undefined;

  // Cost centers of the solicitudes this user created (all dates), offered
  // in the cost center filter besides their own centros_costo_vista
  const solicitados = useReporte<CentroSolicitado[]>(
    () =>
      incluyePropias
        ? api.GET_REPORTES.getSolicitudesCentrosCosto({
            idUsuario: idUsuarioActual,
            limit: 5000,
          })
        : Promise.resolve([]),
    `${incluyePropias}|${idUsuarioActual}`,
  );
  const centrosPropios = useMemo(() => {
    const permitidos = new Set(alcance.centros);
    return Array.from(
      new Set(
        (solicitados.data ?? [])
          .map((c) => Number(c.idCentroCosto))
          .filter((id) => id && !permitidos.has(id)),
      ),
    );
  }, [solicitados.data, alcance]);

  const [form, setForm] = useState<FiltrosForm>(defaultFiltros);
  const [applied, setApplied] = useState<FiltrosForm>(defaultFiltros);

  const query = useMemo(
    () => toQuery(applied, alcance, idSolicitante),
    [applied, alcance, idSolicitante],
  );
  const queryKey = JSON.stringify(query);
  const isDirty =
    JSON.stringify(toQuery(form, alcance, idSolicitante)) !== queryKey;

  // Applies a partial change to both the form and the applied filters
  const patchBoth = useCallback((patch: Partial<FiltrosForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    setApplied((f) => ({ ...f, ...patch }));
  }, []);

  const apply = () => setApplied({ ...form });

  const clear = () => {
    const initial = defaultFiltros();
    setForm(initial);
    setApplied(initial);
  };

  const filterByUsuario = useCallback(
    (idUsuario: number | null | undefined, nombre: string) => {
      if (!idUsuario) return;
      patchBoth({ idUsuario, usuarioNombre: clean(nombre) });
    },
    [patchBoth],
  );

  const filterByProveedor = useCallback(
    (idProveedor: number | null | undefined, nombre: string) => {
      if (!idProveedor) return;
      patchBoth({ idProveedor, proveedorNombre: clean(nombre) });
    },
    [patchBoth],
  );

  const filterByCentroCosto = useCallback(
    (idCentroCosto: number | null | undefined) => {
      if (!idCentroCosto) return;
      if (
        !alcance.total &&
        !alcance.centros.includes(idCentroCosto) &&
        !centrosPropios.includes(idCentroCosto)
      )
        return;
      patchBoth({ centrosCosto: [idCentroCosto] });
    },
    [patchBoth, alcance, centrosPropios],
  );

  const activeCount = [
    applied.centrosCosto.length ? 1 : null,
    applied.idArea,
    applied.idTipoDocumento,
    applied.idUsuario,
    applied.idProveedor,
    applied.idSolicitud,
    applied.nroOrdenCompra,
  ].filter((v) => v !== null).length;

  return {
    alcance,
    // Logged-in user (initData.id)
    idUsuarioActual,
    // Extra cost centers (outside alcance) where the user has solicitudes
    centrosPropios,
    incluyePropias,
    form,
    setForm,
    applied,
    query,
    queryKey,
    isDirty,
    apply,
    clear,
    patchBoth,
    filterByUsuario,
    filterByProveedor,
    filterByCentroCosto,
    activeCount,
  };
}

export type FiltrosReporte = ReturnType<typeof useFiltrosReporte>;

/* ------------------------------------------------------------------ */
/* Data hooks                                                           */
/* ------------------------------------------------------------------ */

/**
 * Loads one report and re-runs whenever `key` changes. Responses from
 * superseded requests are discarded so fast filter changes can't race.
 */
/**
 * ordenes/estados returns { porOrden, porLinea } (per OC and per detail
 * line); older backends return the plain per-OC array. Every screen that
 * reads it goes through here so both shapes work.
 */
export interface EstadosOrden<T> {
  porOrden: T[];
  porLinea: T[] | null;
}

export const normalizarEstadosOrden = <T>(raw: unknown): EstadosOrden<T> => {
  if (Array.isArray(raw)) return { porOrden: raw as T[], porLinea: null };
  const r = (raw ?? {}) as Partial<EstadosOrden<T>>;
  return {
    porOrden: Array.isArray(r.porOrden) ? r.porOrden : [],
    porLinea: Array.isArray(r.porLinea) ? r.porLinea : null,
  };
};

export function useReporte<T>(fetcher: () => Promise<T>, key: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (id === requestId.current) setData(result ?? null);
    } catch (e) {
      console.error("Error cargando reporte:", e);
      if (id === requestId.current) {
        setData(null);
        setError(errorMessage(e));
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [key, load]);

  return { data, loading, error, reload: load };
}

export type Reporte<T> = ReturnType<typeof useReporte<T>>;

/**
 * The app shell (.mainBoxContainer) is 100vw wide, which includes the
 * vertical scrollbar, so on long pages the scrollbar covers the right
 * padding. Publishes the scrollbar width as --rp-scrollbar-width so the
 * dashboards can add it back (see .rp-dashboard in reportes.css).
 *
 * The scrollbar space is reserved while the screen is open
 * (scrollbar-gutter: stable): otherwise the padding change could make the
 * scrollbar appear / disappear again in an endless loop (tables "vibrating",
 * mostly at browser zoom levels other than 100%).
 */
export function useScrollbarGutter() {
  useEffect(() => {
    const root = document.documentElement;
    const gutterAnterior = root.style.scrollbarGutter;
    root.style.scrollbarGutter = "stable";
    const update = () => {
      const width = `${Math.max(0, window.innerWidth - root.clientWidth)}px`;
      // Only write when it really changes (no extra relayouts)
      if (root.style.getPropertyValue("--rp-scrollbar-width") !== width)
        root.style.setProperty("--rp-scrollbar-width", width);
    };
    update();
    window.addEventListener("resize", update);
    // The scrollbar appears / disappears as the reports finish loading
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
      root.style.removeProperty("--rp-scrollbar-width");
      root.style.scrollbarGutter = gutterAnterior;
    };
  }, []);
}

// Filter catalogs (loaded once per screen; failure only empties the options)
export function useCatalogos() {
  return useReporte<Catalogos>(() => api.GET_REPORTES.getCatalogos(), "");
}

/* ------------------------------------------------------------------ */
/* Excel                                                                */
/* ------------------------------------------------------------------ */

export interface ExcelSheet {
  name: string;
  rows: Record<string, unknown>[];
}

export async function exportSheets(fileBase: string, sheets: ExcelSheet[]) {
  const xlsx = await import("xlsx");
  const wb = xlsx.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const sheet = xlsx.utils.json_to_sheet(
      rows.length ? rows : [{ Info: "Sin datos" }],
    );
    // Excel sheet names are limited to 31 characters
    xlsx.utils.book_append_sheet(wb, sheet, name.substring(0, 31));
  });
  const stamp = new Date().toLocaleDateString("es-CL").split("-").join("");
  xlsx.writeFile(wb, `${fileBase}_${stamp}.xlsx`);
}

// Filters sheet shared by both exports
export const filtrosSheet = (
  f: FiltrosForm,
  catalogos: Catalogos | null,
  alcance?: AlcanceReportes,
  incluyePropias = false,
  textoPropias = "sus solicitudes en otros centros",
): ExcelSheet => {
  const ccNames = f.centrosCosto.map(
    (id) =>
      clean(catalogos?.centrosCosto.find((c) => c.id === id)?.nombre) ||
      String(id),
  );
  const area = catalogos?.areas.find((a) => a.id === f.idArea)?.nombre;
  const tipo = catalogos?.tiposDocumento.find(
    (t) => t.id === f.idTipoDocumento,
  )?.nombre;
  return {
    name: "Filtros",
    rows: [
      { Filtro: "Fecha desde", Valor: toIsoDate(f.fechaDesde) ?? "-" },
      { Filtro: "Fecha hasta", Valor: toIsoDate(f.fechaHasta) ?? "-" },
      {
        Filtro: "Centros de costo",
        Valor: ccNames.length
          ? ccNames.join(", ")
          : alcance && !alcance.total
            ? `Todos los del usuario (${alcance.centros.length})${
                incluyePropias ? ` + ${textoPropias}` : ""
              }`
            : "Todos",
      },
      { Filtro: "Área", Valor: f.idArea ? clean(area) || f.idArea : "Todas" },
      {
        Filtro: "Tipo documento",
        Valor: f.idTipoDocumento ? clean(tipo) || f.idTipoDocumento : "Todos",
      },
      {
        Filtro: "Usuario",
        Valor: f.idUsuario ? f.usuarioNombre || f.idUsuario : "Todos",
      },
      {
        Filtro: "Proveedor",
        Valor: f.idProveedor ? f.proveedorNombre || f.idProveedor : "Todos",
      },
      { Filtro: "N° solicitud", Valor: f.idSolicitud ?? "Todas" },
      { Filtro: "N° OC", Valor: f.nroOrdenCompra ?? "Todas" },
      { Filtro: "Generado", Valor: new Date().toLocaleString("es-CL") },
    ],
  };
};
